import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap';

import { AlertAndNotificationsService } from './alert-and-notifications.service';
import { AngularModalService } from './modal.service';
import { ModalConfig } from '../models/modalConfig';
import { CandidateJobMatchesWorkflowActionsModalComponent } from 'src/app/pages/mission-control-page/candidate-job-matches-workflow-page/candidate-job-matches-workflow-actions-modal/candidate-job-matches-workflow-actions-modal.component';

declare var $: any;

/**
 * The service to provide drag and drop functionality of job match cards.
 *
 * The actual card dragging is handled by Angular CDK (see candidate-job-matches-workflow.component
 * and its template) - this service only decides which moves are allowed and which action/modal to
 * trigger on drop, opening the native `CandidateJobMatchesWorkflowActionsModalComponent` for the
 * not-interested, released and move actions.
 */
@Injectable({
  providedIn: 'root'
})
export class JobMatchDragAndDropService {

  constructor(
    private bsModalService: BsModalService,
    private modalService: AngularModalService,
    private alertsAndNotificationsService: AlertAndNotificationsService
  ) { }

  getAllowedDraggableTypesOfCurrentWorkflowStep(currentWorkflowStep: any, enabledSteps: any) {
    let draggableTypes: any[] = [];
    if (currentWorkflowStep === 'Released') {
      draggableTypes = [...enabledSteps];
    } else if (currentWorkflowStep === 'Not Interested') {
      draggableTypes = [...enabledSteps];
    } else {
      draggableTypes = this._getPreviousAndNextWorkflowSteps(enabledSteps, currentWorkflowStep);
    }
    return draggableTypes;
  }

  private _getPreviousAndNextWorkflowSteps(enabledSteps: any, currentWorkflowStep: any) {
    const prevStepIndex = enabledSteps.indexOf(currentWorkflowStep) - 1;
    const prevWorkflowStep = prevStepIndex === -1 ? '' : enabledSteps[prevStepIndex];
    const nextStepIndex = enabledSteps.indexOf(currentWorkflowStep) + 1;
    const nextWorkflowStep = nextStepIndex === enabledSteps.length ? '' : enabledSteps[nextStepIndex];
    return [prevWorkflowStep, currentWorkflowStep, nextWorkflowStep, 'Not Interested', 'Released'];
  }

  dropJobMatchCardToWorkflowStep(jobMatchToDrop: any, droppingToStep: any, enabledSteps: any, refreshCallback: () => void) {
    if (jobMatchToDrop.currentStep === droppingToStep) {
      return;
    }
    const actionToBeExecuted = this._getActionToBeExecutedOnDrop(jobMatchToDrop, droppingToStep, enabledSteps);
    if (actionToBeExecuted.length !== 0) {
      this._performActionOnDrop(actionToBeExecuted[0], jobMatchToDrop, refreshCallback);
    } else {
      let message = 'This action is not allowed.';
      if (droppingToStep === 'Released' || droppingToStep === 'Not Interested' || this._isJobMatchMovedToNextStep(jobMatchToDrop.currentStep, droppingToStep, enabledSteps)) {
        message = `This card cannot be moved to <span class='emerald'>'${droppingToStep}'</span> as it is in <span class='emerald'>'${jobMatchToDrop.jobMatchCurrentState}'</span> state.`;
      } else {
        message = `This card cannot be moved back to <span class='emerald'>'${droppingToStep}'</span> as it is in <span class='emerald'>'${jobMatchToDrop.jobMatchCurrentState}'</span> state.`;
      }
      this.alertsAndNotificationsService.showAlert('Action Not Allowed!', message, 'warning');
    }
  }

  private _getActionToBeExecutedOnDrop(jobMatchToDrop: any, droppingToStep: any, enabledWorkflowSteps: any) {
    let action: any[] = [];

    if (droppingToStep === 'Not Interested') {
      action = jobMatchToDrop.availableActions.filter((a: any) => a.actionType.includes('NOT_INTERESTED'));
      if (action.length > 0) {
        return action;
      }
    }

    if (droppingToStep === 'Released') {
      action = jobMatchToDrop.availableActions.filter((a: any) => a.actionType.includes('RELEASE'));
      if (action.length > 0) {
        return action;
      }
    }

    const indexOfDroppingToStep = enabledWorkflowSteps.indexOf(droppingToStep);
    const indexOfCandidateStep = enabledWorkflowSteps.indexOf(jobMatchToDrop.currentStep);
    if (indexOfDroppingToStep < indexOfCandidateStep) {
      action = jobMatchToDrop.availableActions.filter((a: any) => a.actionType.includes('MOVE_BACKWARD'));
      if (action.length > 0) {
        return action;
      }
    } else {
      action = jobMatchToDrop.availableActions.filter((a: any) => a.actionType.includes('SKIP'));
      if (action.length > 0) {
        return action;
      }

      action = jobMatchToDrop.availableActions.filter((a: any) => a.actionType.includes('MOVE_FORWARD'));
      if (action.length > 0) {
        return action;
      }
    }
    return action;
  }

  private _performActionOnDrop(actionObject: any, jobMatchToDrop: any, refreshCallback: () => void) {
    if (!actionObject.allowed) {
      const message = _.isNull(actionObject.message) ? 'This action is not allowed.' : actionObject.message;
      this.alertsAndNotificationsService.showAlert('Action Not Allowed!', message, 'warning');
      return;
    }

    const actionType = actionObject.actionType;
    const label = actionObject.action;
    switch (actionType) {
      case 'NOT_INTERESTED':
      case 'RELEASE':
        this._launchNotInterestedOrReleasedModal(label, jobMatchToDrop, refreshCallback);
        break;
      case 'MOVE_BACKWARD':
      case 'MOVE_FORWARD':
      case 'SKIP_RECRUITER_SCREENING':
      case 'SKIP_TECHNICAL_ASSESSMENT':
      case 'SKIP_VALUE_ASSESSMENT':
      case 'SKIP_PHONE_SCREEN':
      case 'SKIP_INTERVIEW':
        this._setDataAndLaunchMoveJobMatchModal(actionType, label, jobMatchToDrop, refreshCallback);
        break;
      default:
      // do nothing
    }
  }

  private _isJobMatchMovedToNextStep(currentStep: any, droppingToStep: any, enabledWorkflowSteps: any) {
    return enabledWorkflowSteps.indexOf(droppingToStep) > enabledWorkflowSteps.indexOf(currentStep);
  }

  private _launchNotInterestedOrReleasedModal(modalType: any, jobMatchToDrop: any, refreshCallback: () => void) {
    const action = modalType === 'Not Interested' ? 'notInterested' : 'release';
    const jobMatchDetailsArray = [{
      jobMatchId: jobMatchToDrop.jobMatchId,
      candidateId: jobMatchToDrop.id,
      workflowStepName: jobMatchToDrop.currentStep
    }];
    this._openActionsModal(action, {
      jobMatchDetailsArray,
      statusType: modalType
    }, 'four-dot-five-modal-large', refreshCallback);
  }

  private _setDataAndLaunchMoveJobMatchModal(modalType: any, label: any, jobMatchToDrop: any, refreshCallback: () => void) {
    let windowTopClass = 'four-dot-five-modal-medium';
    let moveType = 'Forward';
    if (modalType === 'MOVE_BACKWARD') {
      moveType = 'Backward';
    } else if (['SKIP_RECRUITER_SCREENING', 'SKIP_TECHNICAL_ASSESSMENT', 'SKIP_VALUE_ASSESSMENT', 'SKIP_PHONE_SCREEN', 'SKIP_INTERVIEW'].indexOf(modalType) !== -1) {
      moveType = 'Skip';
      windowTopClass = 'four-dot-five-modal-small';
    }

    this._openActionsModal('move', {
      jobMatchId: jobMatchToDrop.jobMatchId,
      candidateId: jobMatchToDrop.id,
      moveType,
      label
    }, windowTopClass, refreshCallback);
  }

  private _openActionsModal(action: string, extraResolve: any, windowTopClass: string, refreshCallback: () => void) {
    this.modalService.addModalOpenClassToBodyIfAnyModalIsOpen();
    let bsModalRef: BsModalRef;
    const config = new ModalConfig();
    config.class = windowTopClass;
    config.initialState = {
      resolve: {
        action,
        ...extraResolve
      },
      modalInstance: {
        close: (callbackObject: any) => {
          bsModalRef.hide();
          $('.modal').css('overflow-y', 'auto');
          if (callbackObject && callbackObject.callbackType === 'refresh' && refreshCallback) {
            refreshCallback();
          }
        },
        dismiss: () => {
          bsModalRef.hide();
          $('.modal').css('overflow-y', 'auto');
        }
      }
    };
    bsModalRef = this.bsModalService.show(CandidateJobMatchesWorkflowActionsModalComponent, config);
  }

  isMoveBackActionFromNotInterestedOrReleasedStep(jobMatchToDrop: any) {
    let isValidAction = false;
    if (jobMatchToDrop.currentStep === 'Not Interested' || jobMatchToDrop.currentStep === 'Released') {
      const stepToDropTo = jobMatchToDrop.availableActions.filter((action: any) => action.actionType === 'MOVE_BACKWARD');
      if (stepToDropTo.length > 0) {
        isValidAction = true;
      }
    }
    return isValidAction;
  }
}
